import type { HeldGift, Member } from "@app/protocol";
import { useT } from "../i18n/context";
import { send } from "../ws/singleton";

/** Where a held gift sits on the circle: ten o'clock, opposite the status badge at two. */
export function giftSpot(size: number): { left: number; top: number; side: number } {
  const r = size / 2;
  return {
    left: r - r * Math.cos(Math.PI / 6),
    top: r - r * Math.sin(Math.PI / 6),
    side: Math.max(26, Math.round(size * 0.4)),
  };
}

/**
 * A gift on a player's picture (review 0022, the owner's Zynga brief): it stays until its holder taps
 * it away or gets another. For the holder it is a button; for everyone else a picture.
 */
export function GiftBadge({
  held,
  size,
  mine,
  sender,
}: {
  held: HeldGift;
  size: number;
  mine: boolean;
  sender: Pick<Member, "name"> | null;
}) {
  const { t } = useT();
  const spot = giftSpot(size);
  const name = t(`gift.${held.gift}`);
  const style = { left: spot.left, top: spot.top, width: spot.side, height: spot.side };
  const picture = (
    <img
      src={`/gifts/${held.gift}.webp`}
      alt=""
      draggable={false}
      className="h-full w-full object-contain drop-shadow-[0_3px_6px_rgb(0_0_0/0.55)]"
    />
  );
  const label = t("gift.held", { gift: name, name: sender?.name ?? "?" });
  if (!mine)
    return (
      <span
        role="img"
        aria-label={label}
        className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-1/2"
        style={style}
      >
        {picture}
      </span>
    );
  return (
    <button
      type="button"
      aria-label={t("gift.clear", { gift: name })}
      title={label}
      className="absolute z-10 -translate-x-1/2 -translate-y-1/2 rounded-full"
      style={style}
      onClick={() => send({ type: "clearGift" })}
    >
      {picture}
    </button>
  );
}
